import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  DollarSign, Users, Calendar, Download, FileText,
  Check, X, Clock, Calculator, Banknote, Receipt,
  TrendingUp, TrendingDown, RefreshCw, Eye, Printer,
  ChevronLeft, ChevronRight, Search, Filter, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: 'يناير' },
  { value: 2, label: 'فبراير' },
  { value: 3, label: 'مارس' },
  { value: 4, label: 'أبريل' },
  { value: 5, label: 'مايو' },
  { value: 6, label: 'يونيو' },
  { value: 7, label: 'يوليو' },
  { value: 8, label: 'أغسطس' },
  { value: 9, label: 'سبتمبر' },
  { value: 10, label: 'أكتوبر' },
  { value: 11, label: 'نوفمبر' },
  { value: 12, label: 'ديسمبر' }
];

export default function Payroll() {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    loadPayrolls();
    loadEmployees();
    loadStats();
  }, [selectedMonth, selectedYear]);

  const loadPayrolls = async () => {
    try {
      const res = await api.get(`/payroll/employees?month=${selectedMonth}&year=${selectedYear}`);
      setPayrolls(res.data);
    } catch (error) {
      console.error('Error loading payrolls');
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await api.get('/hr/employees');
      setEmployees(res.data);
    } catch (error) {
      console.error('Error loading employees');
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get(`/payroll/stats?month=${selectedMonth}&year=${selectedYear}`);
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats');
    }
  };

  const generatePayroll = async () => {
    if (!window.confirm(`هل أنت متأكد من توليد رواتب شهر ${MONTHS[selectedMonth - 1].label} ${selectedYear}؟`)) {
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(`/payroll/generate?month=${selectedMonth}&year=${selectedYear}`);
      toast.success(res.data.message);
      loadPayrolls();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  const approvePayroll = async (payrollId) => {
    try {
      await api.put(`/payroll/approve/${payrollId}?approved_by=admin`);
      toast.success('تم اعتماد الراتب');
      loadPayrolls();
      loadStats();
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  const markAsPaid = async (payrollId) => {
    try {
      await api.put(`/payroll/pay/${payrollId}?payment_method=bank_transfer`);
      toast.success('تم تسجيل صرف الراتب');
      loadPayrolls();
      loadStats();
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  const downloadSalarySlip = (payrollId) => {
    window.open(`${BACKEND_URL}/api/payroll/salary-slip/${payrollId}/pdf`, '_blank');
  };

  const downloadPaymentOrder = (payrollId) => {
    window.open(`${BACKEND_URL}/api/payroll/payment-order/${payrollId}/pdf`, '_blank');
  };

  const downloadMonthlyReport = async () => {
    try {
      toast.loading('جاري تحميل مسير الرواتب...', { id: 'download' });
      
      const response = await fetch(`${BACKEND_URL}/api/payroll/monthly-report/${selectedMonth}/${selectedYear}/pdf`);
      if (!response.ok) {
        toast.dismiss('download');
        const error = await response.json().catch(() => ({ detail: 'فشل تحميل المسير' }));
        toast.error(error.detail || 'فشل تحميل المسير');
        return;
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `مسير_الرواتب_${selectedMonth}_${selectedYear}.pdf`;
      
      // Append to body, click, and cleanup
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.dismiss('download');
      toast.success('تم تحميل مسير الرواتب بنجاح');
    } catch (error) {
      toast.dismiss('download');
      console.error('Download error:', error);
      toast.error('حدث خطأ أثناء تحميل المسير');
    }
  };

  const getEmployeeName = (empCode) => {
    const emp = employees.find(e => e.employee_code === empCode);
    return emp?.employee_name_ar || empCode;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">مسودة</Badge>;
      case 'approved':
        return <Badge className="bg-blue-500 hover:bg-blue-600">معتمد</Badge>;
      case 'paid':
        return <Badge className="bg-green-500 hover:bg-green-600">مدفوع</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredPayrolls = payrolls.filter(p => {
    const empName = getEmployeeName(p.employee_code).toLowerCase();
    const matchesSearch = empName.includes(searchTerm.toLowerCase()) || 
                          p.employee_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openEditDialog = (payroll) => {
    setSelectedPayroll({ ...payroll });
    setEditDialogOpen(true);
  };

  const openDetailsDialog = (payroll) => {
    setSelectedPayroll(payroll);
    setDetailsDialogOpen(true);
  };

  const handleSavePayroll = async () => {
    try {
      await api.put(`/payroll/employees/${selectedPayroll.payroll_id}`, selectedPayroll);
      toast.success('تم حفظ التعديلات');
      setEditDialogOpen(false);
      loadPayrolls();
      loadStats();
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Banknote className="h-7 w-7 text-emerald-600" />
            نظام الرواتب
          </h1>
          <p className="text-muted-foreground">إدارة رواتب الموظفين وكشوف المرتبات</p>
        </div>
      </div>

      {/* Period Selector */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>الشهر:</Label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="h-10 px-3 border rounded-lg bg-white"
              data-testid="month-selector"
            >
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Label>السنة:</Label>
            <Input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-24"
              min={2020}
              max={2030}
              data-testid="year-selector"
            />
          </div>
          <div className="flex-1" />
          <Button
            onClick={generatePayroll}
            disabled={loading || payrolls.length > 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="generate-payroll-btn"
          >
            <Calculator className="w-4 h-4 me-2" />
            {loading ? 'جاري التوليد...' : 'توليد الرواتب'}
          </Button>
          {payrolls.length > 0 && (
            <Button variant="outline" onClick={downloadMonthlyReport} data-testid="download-report-btn">
              <Download className="w-4 h-4 me-2" />
              تحميل مسير الرواتب
            </Button>
          )}
        </div>
      </Card>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">عدد الموظفين</p>
                <p className="text-2xl font-bold">{stats.total_employees}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">إجمالي الاستحقاقات</p>
                <p className="text-2xl font-bold">{stats.total_earnings?.toLocaleString()} <span className="text-sm">ر.س</span></p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">إجمالي الخصومات</p>
                <p className="text-2xl font-bold">{stats.total_deductions?.toLocaleString()} <span className="text-sm">ر.س</span></p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">صافي الرواتب</p>
                <p className="text-2xl font-bold">{stats.total_net?.toLocaleString()} <span className="text-sm">ر.س</span></p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Status Summary */}
      {stats && stats.total_employees > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-muted-foreground">مسودة</p>
            <p className="text-xl font-bold text-yellow-600">{stats.draft_count}</p>
          </Card>
          <Card className="p-4 border-l-4 border-blue-500">
            <p className="text-sm text-muted-foreground">معتمد</p>
            <p className="text-xl font-bold text-blue-600">{stats.approved_count}</p>
          </Card>
          <Card className="p-4 border-l-4 border-green-500">
            <p className="text-sm text-muted-foreground">مدفوع</p>
            <p className="text-xl font-bold text-green-600">{stats.paid_count}</p>
          </Card>
        </div>
      )}

      {/* Payroll List */}
      <Card className="p-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="بحث بالاسم أو الكود..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-10"
              data-testid="search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 border rounded-lg bg-white"
            data-testid="status-filter"
          >
            <option value="all">جميع الحالات</option>
            <option value="draft">مسودة</option>
            <option value="approved">معتمد</option>
            <option value="paid">مدفوع</option>
          </select>
        </div>

        {/* Table */}
        {payrolls.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">لا توجد رواتب لهذا الشهر</p>
            <p className="text-sm text-muted-foreground mb-4">اضغط على "توليد الرواتب" لإنشاء رواتب الشهر</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payroll-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-3 text-start">#</th>
                  <th className="p-3 text-start">الموظف</th>
                  <th className="p-3 text-center">الراتب الأساسي</th>
                  <th className="p-3 text-center">البدلات</th>
                  <th className="p-3 text-center">الخصومات</th>
                  <th className="p-3 text-center">صافي الراتب</th>
                  <th className="p-3 text-center">الحالة</th>
                  <th className="p-3 text-center">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayrolls.map((payroll, idx) => (
                  <tr key={payroll.payroll_id} className="border-b hover:bg-muted/30">
                    <td className="p-3">{idx + 1}</td>
                    <td className="p-3">
                      <div>
                        <p className="font-medium">{getEmployeeName(payroll.employee_code)}</p>
                        <p className="text-xs text-muted-foreground">{payroll.employee_code}</p>
                      </div>
                    </td>
                    <td className="p-3 text-center font-mono">{payroll.basic_salary?.toLocaleString()}</td>
                    <td className="p-3 text-center font-mono text-green-600">
                      +{(payroll.total_earnings - payroll.basic_salary)?.toLocaleString()}
                    </td>
                    <td className="p-3 text-center font-mono text-red-600">
                      -{payroll.total_deductions?.toLocaleString()}
                    </td>
                    <td className="p-3 text-center font-bold text-emerald-600">
                      {payroll.net_salary?.toLocaleString()}
                    </td>
                    <td className="p-3 text-center">{getStatusBadge(payroll.status)}</td>
                    <td className="p-3">
                      <div className="flex justify-center gap-1 flex-wrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDetailsDialog(payroll)}
                          title="عرض التفاصيل"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {payroll.status === 'draft' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(payroll)}
                              title="تعديل"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-600"
                              onClick={() => approvePayroll(payroll.payroll_id)}
                              title="اعتماد"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {payroll.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-600"
                            onClick={() => markAsPaid(payroll.payroll_id)}
                            title="تسجيل الصرف"
                          >
                            <Banknote className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadSalarySlip(payroll.payroll_id)}
                          title="كشف الراتب PDF"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-orange-600"
                          onClick={() => downloadPaymentOrder(payroll.payroll_id)}
                          title="أمر الصرف PDF"
                        >
                          <Receipt className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted font-bold">
                  <td colSpan="2" className="p-3">الإجمالي ({filteredPayrolls.length} موظف)</td>
                  <td className="p-3 text-center">
                    {filteredPayrolls.reduce((sum, p) => sum + (p.basic_salary || 0), 0).toLocaleString()}
                  </td>
                  <td className="p-3 text-center text-green-600">
                    +{filteredPayrolls.reduce((sum, p) => sum + ((p.total_earnings || 0) - (p.basic_salary || 0)), 0).toLocaleString()}
                  </td>
                  <td className="p-3 text-center text-red-600">
                    -{filteredPayrolls.reduce((sum, p) => sum + (p.total_deductions || 0), 0).toLocaleString()}
                  </td>
                  <td className="p-3 text-center text-emerald-600">
                    {filteredPayrolls.reduce((sum, p) => sum + (p.net_salary || 0), 0).toLocaleString()}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              تفاصيل الراتب - {getEmployeeName(selectedPayroll?.employee_code)}
            </DialogTitle>
          </DialogHeader>
          {selectedPayroll && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">كود الموظف:</span>
                    <span className="font-mono ms-2">{selectedPayroll.employee_code}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">الشهر:</span>
                    <span className="ms-2">{MONTHS[selectedPayroll.month - 1]?.label} {selectedPayroll.year}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">الحالة:</span>
                    <span className="ms-2">{getStatusBadge(selectedPayroll.status)}</span>
                  </div>
                </div>
              </div>

              {/* Earnings */}
              <div>
                <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  الاستحقاقات
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between p-2 bg-green-50 rounded">
                    <span>الراتب الأساسي</span>
                    <span className="font-mono">{selectedPayroll.basic_salary?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>بدل السكن</span>
                    <span className="font-mono">{selectedPayroll.housing_allowance?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>بدل المواصلات</span>
                    <span className="font-mono">{selectedPayroll.transport_allowance?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>بدل الطعام</span>
                    <span className="font-mono">{selectedPayroll.food_allowance?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>بدلات أخرى</span>
                    <span className="font-mono">{selectedPayroll.other_allowances?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>مكافأة</span>
                    <span className="font-mono">{selectedPayroll.bonus?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2 bg-green-100 font-bold rounded">
                    <span>إجمالي الاستحقاقات</span>
                    <span className="font-mono text-green-700">{selectedPayroll.total_earnings?.toLocaleString()} ر.س</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <TrendingDown className="h-4 w-4" />
                  الخصومات
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between p-2">
                    <span>خصم غياب ({selectedPayroll.absent_days} يوم)</span>
                    <span className="font-mono text-red-600">-{selectedPayroll.absence_deduction?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>خصم تأخير ({selectedPayroll.late_hours} ساعة)</span>
                    <span className="font-mono text-red-600">-{selectedPayroll.late_deduction?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>تأمينات اجتماعية (GOSI)</span>
                    <span className="font-mono text-red-600">-{selectedPayroll.gosi_deduction?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>تأمين طبي</span>
                    <span className="font-mono text-red-600">-{selectedPayroll.insurance_deduction?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>خصم سلفة</span>
                    <span className="font-mono text-red-600">-{selectedPayroll.advance_deduction?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2">
                    <span>خصومات أخرى</span>
                    <span className="font-mono text-red-600">-{selectedPayroll.other_deductions?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between p-2 bg-red-100 font-bold rounded">
                    <span>إجمالي الخصومات</span>
                    <span className="font-mono text-red-700">-{selectedPayroll.total_deductions?.toLocaleString()} ر.س</span>
                  </div>
                </div>
              </div>

              {/* Net Salary */}
              <div className="p-4 bg-emerald-100 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">صافي الراتب المستحق</span>
                  <span className="text-2xl font-bold text-emerald-700">{selectedPayroll.net_salary?.toLocaleString()} ر.س</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => downloadSalarySlip(selectedPayroll.payroll_id)}>
                  <Download className="h-4 w-4 me-2" />
                  كشف الراتب PDF
                </Button>
                <Button variant="outline" className="text-orange-600 border-orange-300" onClick={() => downloadPaymentOrder(selectedPayroll.payroll_id)}>
                  <Receipt className="h-4 w-4 me-2" />
                  أمر الصرف PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              تعديل راتب - {getEmployeeName(selectedPayroll?.employee_code)}
            </DialogTitle>
          </DialogHeader>
          {selectedPayroll && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>الراتب الأساسي</Label>
                  <Input
                    type="number"
                    value={selectedPayroll.basic_salary || 0}
                    onChange={(e) => setSelectedPayroll({...selectedPayroll, basic_salary: parseFloat(e.target.value) || 0})}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>بدل السكن</Label>
                  <Input
                    type="number"
                    value={selectedPayroll.housing_allowance || 0}
                    onChange={(e) => setSelectedPayroll({...selectedPayroll, housing_allowance: parseFloat(e.target.value) || 0})}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>بدل المواصلات</Label>
                  <Input
                    type="number"
                    value={selectedPayroll.transport_allowance || 0}
                    onChange={(e) => setSelectedPayroll({...selectedPayroll, transport_allowance: parseFloat(e.target.value) || 0})}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>بدل الطعام</Label>
                  <Input
                    type="number"
                    value={selectedPayroll.food_allowance || 0}
                    onChange={(e) => setSelectedPayroll({...selectedPayroll, food_allowance: parseFloat(e.target.value) || 0})}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>بدلات أخرى</Label>
                  <Input
                    type="number"
                    value={selectedPayroll.other_allowances || 0}
                    onChange={(e) => setSelectedPayroll({...selectedPayroll, other_allowances: parseFloat(e.target.value) || 0})}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>مكافأة</Label>
                  <Input
                    type="number"
                    value={selectedPayroll.bonus || 0}
                    onChange={(e) => setSelectedPayroll({...selectedPayroll, bonus: parseFloat(e.target.value) || 0})}
                    className="font-mono"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-red-700 mb-3">الخصومات</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>خصم غياب</Label>
                    <Input
                      type="number"
                      value={selectedPayroll.absence_deduction || 0}
                      onChange={(e) => setSelectedPayroll({...selectedPayroll, absence_deduction: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                    />
                  </div>
                  <div>
                    <Label>خصم تأخير</Label>
                    <Input
                      type="number"
                      value={selectedPayroll.late_deduction || 0}
                      onChange={(e) => setSelectedPayroll({...selectedPayroll, late_deduction: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                    />
                  </div>
                  <div>
                    <Label>تأمينات اجتماعية</Label>
                    <Input
                      type="number"
                      value={selectedPayroll.gosi_deduction || 0}
                      onChange={(e) => setSelectedPayroll({...selectedPayroll, gosi_deduction: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                    />
                  </div>
                  <div>
                    <Label>خصم سلفة</Label>
                    <Input
                      type="number"
                      value={selectedPayroll.advance_deduction || 0}
                      onChange={(e) => setSelectedPayroll({...selectedPayroll, advance_deduction: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                    />
                  </div>
                  <div>
                    <Label>خصومات أخرى</Label>
                    <Input
                      type="number"
                      value={selectedPayroll.other_deductions || 0}
                      onChange={(e) => setSelectedPayroll({...selectedPayroll, other_deductions: parseFloat(e.target.value) || 0})}
                      className="font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  إلغاء
                </Button>
                <Button onClick={handleSavePayroll} className="bg-emerald-600 hover:bg-emerald-700">
                  حفظ التعديلات
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
